import {useEffect, useState} from "react";
import {Cloneable} from "@/types";
import {getChangedPaths} from "@/utils/objectUtils";

export type HistoryItem<T> = {
    changes: { index: number; oldItem: T }[];
};

const useHistory = <T extends Cloneable>(
    coalesceFields?: string[]
) => {
    const [items, setItems] = useState<T[]>([]);
    const [history, setHistory] = useState<HistoryItem<T>[]>([]);
    const [redoHistory, setRedoHistory] = useState<HistoryItem<T>[]>([]);


    const setItemsWithReset = (newItems: T[]) => {
        setItems(newItems);
        setHistory([]);
        setRedoHistory([]);
    }

    const shouldCoalesce = (oldItem: T, newItem: T): boolean => {
        if (!coalesceFields || coalesceFields.length === 0) {
            return true;
        }

        const changedPaths = getChangedPaths(oldItem, newItem);

        for (const path of changedPaths) {
            const isCoalescable = coalesceFields.some(field =>
                path === field || path.startsWith(field + '.')
            );
            if (!isCoalescable) {
                return false;
            }
        }

        return true;
    };

    const updateItems = (changes: { index: number; item: T }[]) => {
        const newItems = [...items];
        const historyChanges: { index: number; oldItem: T }[] = [];

        changes.forEach(({index, item}) => {
            const oldItem = newItems[index].clone();
            newItems[index] = item;
            historyChanges.push({index, oldItem});
        });

        setItems(newItems);
        setRedoHistory([]);

        setHistory((prevHistory) => {
            if (prevHistory.length === 0) {
                return [{changes: historyChanges}];
            }

            const lastHistoryItem = prevHistory[prevHistory.length - 1];

            if (
                lastHistoryItem.changes.length === 1 &&
                historyChanges.length === 1 &&
                lastHistoryItem.changes[0].index === historyChanges[0].index &&
                shouldCoalesce(historyChanges[0].oldItem, changes[0].item)
            ) {
                return [
                    ...prevHistory.slice(0, -1),
                    {changes: [{...historyChanges[0], oldItem: lastHistoryItem.changes[0].oldItem}]},
                ];
            }

            return [...prevHistory, {changes: historyChanges}];
        });
    };

    const addItem = (item: T) => {
        const newItems = [...items, item];
        setItems(newItems);
        setRedoHistory([]);
        setHistory(prevHistory => {
            const deletedItem = item.clone();
            (deletedItem as any).deleted = true;
            return [...prevHistory, {changes: [{index: newItems.length - 1, oldItem: deletedItem}]}];
        });
    }

    const undo = () => {
        if (history.length > 0) {
            const lastHistoryItem = history[history.length - 1];
            const newItems = [...items];
            const redoChanges: { index: number; oldItem: T }[] = [];

            lastHistoryItem.changes.forEach(({index, oldItem}) => {
                redoChanges.push({index, oldItem: newItems[index].clone()});
                newItems[index] = oldItem;
            });

            setItems(newItems);
            setHistory((prevHistory) => prevHistory.slice(0, -1));
            setRedoHistory((prevRedoHistory) => [...prevRedoHistory, {changes: redoChanges}]);
        }
    };

    const redo = () => {
        if (redoHistory.length > 0) {
            const lastRedoItem = redoHistory[redoHistory.length - 1];
            const newItems = [...items];
            const historyChanges: { index: number; oldItem: T }[] = [];

            lastRedoItem.changes.forEach(({index, oldItem}) => {
                historyChanges.push({index, oldItem: newItems[index].clone()});
                newItems[index] = oldItem;
            });

            setItems(newItems);
            setRedoHistory((prevRedoHistory) => prevRedoHistory.slice(0, -1));
            setHistory((prevHistory) => [...prevHistory, {changes: historyChanges}]);
        }
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.key === "z") {
                event.preventDefault();
                undo();
            } else if ((event.ctrlKey || event.metaKey) && event.key === "y") {
                event.preventDefault();
                redo();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [history, redoHistory]);

    return {
        items,
        setItems: setItemsWithReset,
        updateItems,
        addItem,
        undo: history.length ? undo : null,
        redo: redoHistory.length ? redo : null,
        historyLength: history.length
    };
};

export default useHistory;
